import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePlanPermissions } from '../../hooks/usePlanPermissions';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import { useAIAssistant } from '../../hooks/useAIAssistant';
import { UpgradeModal } from './UpgradeModal';
import { trackEvent } from '../../lib/analytics';

const SUGGESTED_QUESTIONS = [
  'Quanto gastei este mês?',
  'Qual minha maior categoria de gasto?',
  'Como estão minhas metas?',
  'Qual meu saldo atual?',
];

const ADMIN_ROLES = ['master_admin', 'admin', 'support', 'finance'];

export const PandaAIChat = () => {
  const { user, role } = useAuth();
  const { settings } = useSystemSettings();
  const { canUseAiAssistant, aiMonthlyQuestionsLimit } = usePlanPermissions();
  const { messages, status, blockReason, blockMessage, sendMessage, clearMessages } = useAIAssistant();

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [welcomed, setWelcomed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isAdminUser = !!role && ADMIN_ROLES.includes(role);
  // Admins always bypass the global on/off switch — the flag is for end users only
  const aiEnabled = isAdminUser || settings.ai_enabled_global === 'true';
  const assistantName = settings.ai_assistant_name || 'Panda AI';
  const welcomeMessage = settings.ai_welcome_message || `Olá! Sou o ${assistantName}, seu assistente financeiro. Como posso ajudar?`;
  useEffect(() => {
    if (open && !welcomed && messages.length === 0 && aiEnabled && canUseAiAssistant) {
      setWelcomed(true);
    }
  }, [open, welcomed, messages.length, aiEnabled, canUseAiAssistant]);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    trackEvent('ai_opened');
  }, []);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleSend = useCallback(async () => {
    const q = input.trim();
    if (!q || status === 'loading') return;
    setInput('');
    await sendMessage(q);
  }, [input, status, sendMessage]);

  const handleSuggestion = useCallback((q: string) => {
    setInput(q);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  // Don't render if no user
  if (!user) return null;

  const isBlocked = !aiEnabled || !canUseAiAssistant;

  return (
    <>
      {/* Floating Button — fica ACIMA do HelpFAB (bottom-24 = HelpFAB h-12 + gap) */}
      <div className="fixed bottom-24 right-6 z-40">
        {!open && (
          <button
            onClick={handleOpen}
            className="group relative w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 rounded-2xl flex items-center justify-center cursor-pointer transition-all hover:scale-110 active:scale-95 overflow-hidden"
            style={{ boxShadow: '0 8px 32px rgba(16,185,129,0.4)' }}
            title={`${assistantName} — Assistente Financeiro`}
          >
            {settings.ai_button_icon_url ? (
              <img
                src={settings.ai_button_icon_url}
                alt="Panda AI"
                className="w-10 h-10 object-contain rounded-xl"
              />
            ) : (
              <i className="ri-robot-2-line text-2xl text-white" />
            )}
            {isBlocked && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center">
                <i className="ri-lock-line text-[9px] text-white" />
              </span>
            )}
          </button>
        )}
      </div>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-2rem)] flex flex-col bg-white rounded-2xl overflow-hidden"
          style={{ height: '580px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3.5 bg-gradient-to-r from-emerald-500 to-emerald-600 flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center overflow-hidden">
                {settings.ai_button_icon_url ? (
                  <img src={settings.ai_button_icon_url} alt="Panda AI" className="w-7 h-7 object-contain rounded-lg" />
                ) : (
                  <i className="ri-robot-2-line text-white text-base" />
                )}
              </div>
              <div>
                <p className="text-sm font-700 text-white leading-tight">{assistantName}</p>
                <div className="flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${isBlocked ? 'bg-amber-300' : 'bg-emerald-200'}`} />
                  <p className="text-[10px] text-emerald-100">{isBlocked ? 'Bloqueado' : status === 'loading' ? 'Pensando...' : 'Online'}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button onClick={clearMessages} title="Limpar conversa"
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/20 text-white/70 hover:text-white cursor-pointer transition-colors">
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              )}
              <button onClick={handleClose}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/20 text-white/70 hover:text-white cursor-pointer transition-colors">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
            {/* Disabled state */}
            {!aiEnabled && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-robot-2-line text-3xl text-gray-400" />
                </div>
                <p className="text-sm font-600 text-gray-700 mb-1">Panda AI indisponível</p>
                <p className="text-xs text-gray-500">O assistente está temporariamente desativado pelo administrador.</p>
              </div>
            )}

            {/* Plan blocked */}
            {aiEnabled && !canUseAiAssistant && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-lock-line text-3xl text-amber-400" />
                </div>
                <p className="text-sm font-700 text-gray-800 mb-1">Recurso premium</p>
                <p className="text-xs text-gray-500 mb-4 leading-relaxed">O Panda AI não está disponível no seu plano atual. Faça upgrade para conversar com seu assistente financeiro.</p>
                <button onClick={() => { setShowUpgrade(true); trackEvent('ai_blocked_by_plan', { reason: 'plan_feature' }); }}
                  className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors">
                  Ver planos
                </button>
              </div>
            )}

            {/* Chat messages */}
            {aiEnabled && canUseAiAssistant && (
              <>
                {/* Welcome */}
                {messages.length === 0 && (
                  <div className="space-y-3">
                    <div className="flex gap-2.5">
                      <div className="w-7 h-7 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-robot-2-line text-emerald-600 text-sm" />
                      </div>
                      <div className="bg-white rounded-2xl rounded-tl-none px-3.5 py-2.5 text-sm text-gray-700 shadow-sm max-w-[85%] leading-relaxed">
                        {welcomeMessage}
                      </div>
                    </div>
                    {/* Suggestions */}
                    <div className="pl-9 space-y-1.5">
                      <p className="text-xs text-gray-400 mb-2">Perguntas sugeridas:</p>
                      {SUGGESTED_QUESTIONS.map(q => (
                        <button key={q} onClick={() => handleSuggestion(q)}
                          className="block w-full text-left px-3 py-2 bg-white hover:bg-emerald-50 border border-gray-100 hover:border-emerald-200 rounded-xl text-xs text-gray-600 hover:text-emerald-700 cursor-pointer transition-all">
                          {q}
                        </button>
                      ))}
                    </div>
                    {aiMonthlyQuestionsLimit !== null && (
                      <p className="pl-9 text-[11px] text-gray-400">{aiMonthlyQuestionsLimit} perguntas/mês no seu plano</p>
                    )}
                  </div>
                )}

                {messages.map(msg => (
                  <div key={msg.id} className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-7 h-7 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-robot-2-line text-emerald-600 text-sm" />
                      </div>
                    )}
                    <div className={`px-3.5 py-2.5 rounded-2xl text-sm max-w-[80%] leading-relaxed shadow-sm ${
                      msg.role === 'user'
                        ? 'bg-emerald-500 text-white rounded-tr-none'
                        : 'bg-white text-gray-700 rounded-tl-none'
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                ))}

                {/* Loading indicator */}
                {status === 'loading' && (
                  <div className="flex gap-2.5">
                    <div className="w-7 h-7 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
                      <i className="ri-robot-2-line text-emerald-600 text-sm" />
                    </div>
                    <div className="bg-white rounded-2xl rounded-tl-none px-4 py-3 shadow-sm flex items-center gap-1.5">
                      {[0, 1, 2].map(i => (
                        <span key={i} className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Block reason after chat */}
                {blockReason && (
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 text-center">
                    <i className="ri-lock-line text-amber-500 text-lg mb-1 block" />
                    <p className="text-xs font-600 text-amber-700 mb-1">{blockReason === 'limit_reached' ? 'Limite atingido' : 'Acesso bloqueado'}</p>
                    <p className="text-xs text-amber-600 mb-2.5">{blockMessage}</p>
                    <button onClick={() => setShowUpgrade(true)}
                      className="px-4 py-1.5 bg-amber-400 hover:bg-amber-500 text-white text-xs font-600 rounded-xl cursor-pointer whitespace-nowrap">
                      Fazer upgrade
                    </button>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          {aiEnabled && canUseAiAssistant && (
            <div className="px-3 py-3 border-t border-gray-100 bg-white flex-shrink-0">
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                <input ref={inputRef} type="text" value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  placeholder="Pergunte sobre suas finanças..."
                  disabled={status === 'loading' || !!blockReason}
                  className="flex-1 bg-transparent text-sm text-gray-700 placeholder:text-gray-400 outline-none disabled:opacity-50" />
                <button onClick={handleSend}
                  disabled={!input.trim() || status === 'loading' || !!blockReason}
                  className="w-7 h-7 flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 rounded-lg cursor-pointer transition-colors shrink-0">
                  <i className="ri-send-plane-fill text-white text-sm" />
                </button>
              </div>
              <p className="text-[10px] text-gray-400 text-center mt-1.5">Respostas baseadas nos seus dados financeiros respeitando regras de LGPD.</p>
            </div>
          )}
        </div>
      )}

      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)}
        title={`Desbloqueie o ${assistantName}`}
        description={`Converse com seu assistente financeiro inteligente. Pergunte sobre gastos, metas, saldo e receba análises personalizadas.`} />
    </>
  );
};
